import React from 'react'
import { formatBigMoney, formatNumber, formatPercentage } from '../../packages/utils/numberFormatters'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { lowercaseFirstLetter } from '../../utils/stringConversion'
import { calculateIssueTotalOfferingSize, getSummarizedFeeLevels } from '../issues/utils'
import { NewsItem } from 'types/NewsItem'

const getTypeName = (issue: NewsItem, t: TFunction) => {
    const { type, earnout } = issue.issueData

    const isEarnoutIssue = type === 'Non-cash issue' && earnout
    const typeName = isEarnoutIssue
        ? t('entities.issueType.earnout')
        : t(`entities.issueType.values.${type}`).toLowerCase()

    return typeName
}

const NoTermsInitially = ({ row }: { row: NewsItem }) => {
    const { t } = useTranslation()

    return (
        <span>
            {t('pages.news.headlines.publication.noTermsInitially', {
                companyName: row.companyName,
                issueType: getTypeName(row, t),
            })}
        </span>
    )
}

const GuaranteeFeeIssue = ({ row }: { row: NewsItem }) => {
    const { t } = useTranslation()

    return (
        <span>
            {t('pages.news.headlines.publication.guarantorFeeIssue', {
                companyName: row.companyName,
                issueType: t(`entities.issueType.values.${row.issueData.type}`).toLowerCase(),
            })}
        </span>
    )
}

const ABBDirectedIssue = ({ row }: { row: NewsItem }) => {
    const { t, i18n } = useTranslation()
    const locale = i18n.language

    const issueDataType = row.issueData.type
    const abbTarget = row.issueData.abbTarget
    const totalOfferedUnits = (row.issueData.offeredUnits ?? 0) + (row.issueData.totalOfferedUnits ?? 0)
    const currency = row.currency

    return (
        <>
            <span>
                {t('pages.news.headlines.publication.announces', {
                    companyName: row.companyName,
                    issueType: lowercaseFirstLetter(t(`entities.issueType.values.${issueDataType}`)),
                })}
            </span>
            {abbTarget && (
                <span>
                    {' '}
                    {t('pages.news.headlines.publication.approxAmount', {
                        amount: formatBigMoney(abbTarget, { locale, currencySymbol: currency }),
                    })}
                </span>
            )}
            {!abbTarget && totalOfferedUnits && (
                <span>
                    {' '}
                    {t('pages.news.headlines.publication.approxShares', {
                        units: formatNumber(totalOfferedUnits, { locale, decimals: 0 }),
                    })}
                </span>
            )}
        </>
    )
}

const IPOIssue = ({ row }: { row: NewsItem }) => {
    const { t, i18n } = useTranslation()
    const locale = i18n.language

    const companyName = row.companyName
    const currency = row.currency
    const { offeredUnits, offeredUnitsUnlisted, secondaryOffering, ipoIntervalLow, ipoIntervalHigh } = row.issueData
    const totalOfferedUnits =
        offeredUnits === null && offeredUnitsUnlisted === null
            ? null
            : (offeredUnits ?? 0) + (offeredUnitsUnlisted ?? 0)

    const title = (() => {
        if (row.issueData.unitSubPrice !== null) {
            const unitSubPrice = parseFloat(row.issueData.unitSubPrice)
            return t('pages.news.headlines.publication.ipoWithPrice', {
                companyName,
                amount: formatBigMoney((totalOfferedUnits + secondaryOffering) * unitSubPrice, {
                    locale,
                    currencySymbol: currency,
                }),
            })
        } else if (!row.issueData.unitSubPrice && ipoIntervalLow && ipoIntervalHigh) {
            return t('pages.news.headlines.publication.ipoWithInterval', {
                companyName,
                amountLow: formatBigMoney((totalOfferedUnits + secondaryOffering) * ipoIntervalLow, {
                    locale,
                    currencySymbol: currency,
                }),
                amountHigh: formatBigMoney((totalOfferedUnits + secondaryOffering) * ipoIntervalHigh, {
                    locale,
                    currencySymbol: currency,
                }),
            })
        }
    })()

    return <span>{title}</span>
}

const WarrantIssue = ({ row }: { row: NewsItem }) => {
    const { t } = useTranslation()
    const { warrantData } = row

    return (
        <span>
            {t('pages.news.headlines.publication.warrant', {
                companyName: row.companyName,
                listedPrefix: warrantData.unlisted ? t('pages.news.headlines.publication.warrantUnlisted') : '',
                name: row.name || warrantData.name,
            })}
        </span>
    )
}

const ConvertibleIssue = ({ row }: { row: NewsItem }) => {
    const { t, i18n } = useTranslation()
    const locale = i18n.language

    const { convertibleData } = row

    if (convertibleData.creditLine !== null) {
        return (
            <span>
                {t('pages.news.headlines.publication.convertibleCreditLine', {
                    companyName: row.companyName,
                    amount: formatBigMoney(convertibleData.creditLine, {
                        locale,
                        currencySymbol: row.currency,
                    }),
                })}
            </span>
        )
    }

    if (convertibleData.loanAmount !== null) {
        return (
            <span>
                {t('pages.news.headlines.publication.convertibleLoan', {
                    companyName: row.companyName,
                    amount: formatBigMoney(convertibleData.loanAmount, { locale, currencySymbol: row.currency }),
                })}
            </span>
        )
    }
}

const Issue = ({ row }: { row: NewsItem }) => {
    const { issueData, currency, companyName } = row
    const { type, sharesInUnit, hasWarrants, investors } = issueData

    const investorsExcludingSellers = investors.filter((investor) => !investor.isSeller)
    const totalOfferedUnits =
        issueData.offeredUnits === null && issueData.offeredUnitsUnlisted === null
            ? null
            : (issueData.offeredUnits ?? 0) + (issueData.offeredUnitsUnlisted ?? 0)
    const { totalOfferingSize } = calculateIssueTotalOfferingSize({
        ...row.issueData,
        totalOfferedUnits,
    })
    const summarizedFeeLevels = getSummarizedFeeLevels(investorsExcludingSellers, totalOfferingSize)

    const { t, i18n } = useTranslation()
    const locale = i18n.language
    const typeName = getTypeName(row, t)

    const amount =
        (issueData.abbTarget ?? 0) +
        ((issueData.offeredUnits ?? 0) + (issueData.offeredUnitsUnlisted ?? 0)) * issueData.unitSubPrice

    const totalCommitment = summarizedFeeLevels.reduce((acc, fee) => {
        return acc + fee.amountInPercentage
    }, 0)

    const rightsIssueText = (() => {
        let text = ''

        if (amount !== 0) {
            if (sharesInUnit === 0 && hasWarrants) {
                text += t('pages.news.headlines.publication.warrantsOnly')
            } else {
                text += ` ${t('pages.news.headlines.publication.approxAmount', {
                    amount: formatBigMoney(amount, {
                        locale,
                        currencySymbol: currency,
                    }),
                })}`
            }
        }
        text += ` ${t('pages.news.headlines.publication.securedTo', {
            rate: formatPercentage(Math.min(totalCommitment, 100), { locale, decimals: 0 }),
        })}`

        return text
    })()

    return (
        <span>
            {t('pages.news.headlines.publication.announces', { companyName, issueType: typeName })}
            {type === 'Rights issue' ? rightsIssueText : null}
            {type !== 'Rights issue' &&
                amount !== 0 &&
                ` ${t('pages.news.headlines.publication.approxAmount', {
                    amount: formatBigMoney(amount, {
                        locale,
                        currencySymbol: currency,
                    }),
                })}`}
            {type === 'Non-cash issue' &&
                t('pages.news.headlines.publication.regardingNonCash', {
                    consideration: issueData.noncashConsideration,
                })}
        </span>
    )
}

function NewsHeadlinePublication({ row }: { row: NewsItem }) {
    const { issueData } = row

    if (row.entityType === 'Warrant') return <WarrantIssue row={row} />
    if (row.entityType === 'Convertible') return <ConvertibleIssue row={row} />
    else if (issueData.type === 'Guarantor fee issue') return <GuaranteeFeeIssue row={row} />
    else if (issueData.type === 'ABB Directed issue') return <ABBDirectedIssue row={row} />
    else if (issueData.type === 'IPO') return <IPOIssue row={row} />
    else if (issueData.noTermsInitially && issueData.abbTarget === null) return <NoTermsInitially row={row} />
    else return <Issue row={row} />
}

export default NewsHeadlinePublication
